/* ==========================================================================
   Project T.W.I.S.T. — /pages/cc-twist
   Orchestrates the three steps around JotForm's own runtime.

   JotForm still owns: field validation, the birthday datepicker, error
   navigation, and the page 1 -> page 2 transition. This file only:
     1. keeps our decorative chrome in sync with JotForm's current page,
     2. turns the flavor checkbox group into a single-select,
     3. routes the POST into a hidden iframe so the page never navigates away,
       and reveals our own confirmation panel,
     4. wires the share buttons.

   It depends on internals of JotForm revision 811c2fc34c5 (pinned in
   snippets/cc-twist-jotform-runtime.liquid):
     - the "EventObserver-form-submitted" CustomEvent
     - the "js-non-displayed-page" class on hidden page sections
     - JotForm.noJump / JotForm.ignoreDirectValidation
   ========================================================================== */

(function () {
  'use strict';

  var VOTE_SELECTOR = 'input[name="q13_input13[]"]';
  var SUBMIT_TIMEOUT_MS = 12000;

  /* Anything that originates in here needs JotForm's dollar; everything else
     on the page needs the theme's jQuery. See arbitrateDollar(). */
  var JOTFORM_ZONE = '.cc-twist, #calendar_4, .form-error-message, .errorNavigation';

  var DOLLAR_EVENTS = [
    'mousedown', 'mouseup', 'click', 'touchstart', 'touchend',
    'keydown', 'keypress', 'keyup',
    'focusin', 'focusout', 'change', 'input', 'submit'
  ];

  function init() {
    var stage = document.querySelector('[data-cc-twist]');
    if (!stage) return;

    var form = stage.querySelector('form.jotform-form');
    if (!form) return;

    var sink = document.querySelector('iframe[name="cc-twist-sink"]');
    var status = stage.querySelector('[data-cc-twist-status]');
    var pages = stage.querySelectorAll('.form-all > .page-section');

    dropStaleVexBlocker();
    arbitrateDollar();
    primePrototypeReady();
    tameJotFormGlobals();
    watchSteps();
    wireSingleSelect();
    wireVoteGuard();
    wireConsentLinks();
    wireSubmit();
    wireShare();

    /* ----------------------------------------------------------------------
       window.$ arbitration.

       Two libraries claim window.$ on this page and neither can be removed:
         - Prototype, bundled into prototype.forms.js, which JotForm needs.
         - jQuery, which the theme needs (bt-libs.js alone has 436 bare $()
           calls and resolves them at event time, not at registration time).

       layout/theme.liquid renders theme-scripts (jQuery) AFTER
       content_for_layout, so jQuery always wins the global. Prototype's
       Event.fire() does `element = $(element)` and then
       `element.dispatchEvent(event)`, so once jQuery owns $, firing
       'dom:loaded' throws "element.dispatchEvent is not a function". That
       kills JotForm.init() outright -- no handlePages(), no datepicker, and a
       dead "ENTER THE VOTING VAULT" button. (Pre-existing on the live site.)

       Rather than rewrite the theme's JS, hand window.$ to whichever library
       is about to run: JotForm's own jfDollar shim for anything originating
       inside the form or the datepicker, jQuery for everything else. The
       listeners are capture-phase, so the swap lands before either library's
       handlers execute.
       ---------------------------------------------------------------------- */
    function jotFormDollar() {
      return (window.jfDollar && window.jfDollar.$) || null;
    }

    function themeDollar() {
      return window.jQuery || null;
    }

    function arbitrateDollar() {
      var restoreTimer = null;

      function use(dollar) {
        if (dollar && window.$ !== dollar) window.$ = dollar;
      }

      DOLLAR_EVENTS.forEach(function (type) {
        document.addEventListener(type, function (e) {
          var node = e.target;
          if (node && node.nodeType === 3) node = node.parentNode; // text node
          var inZone = !!(node && node.closest && node.closest(JOTFORM_ZONE));

          window.clearTimeout(restoreTimer);

          if (!inZone) {
            use(themeDollar());
            return;
          }

          use(jotFormDollar());

          // JotForm's handlers run synchronously off this event. Once they are
          // done, hand $ back to jQuery: the theme's timers and carousels keep
          // running between interactions, so jQuery is the safer resting state.
          restoreTimer = window.setTimeout(function () {
            use(themeDollar());
          }, 0);
        }, true);
      });
    }

    /* ----------------------------------------------------------------------
       Fire Prototype's 'dom:loaded' now, while $ is still JotForm's.

       This file is deferred and sits above theme-scripts in the document, so
       it executes after the DOM is parsed but BEFORE jQuery loads -- the only
       window in which Prototype can dispatch safely. Setting document.loaded
       first makes Prototype's own DOMContentLoaded handler a no-op, so the
       crash above never happens.
       ---------------------------------------------------------------------- */
    function primePrototypeReady() {
      if (!window.Prototype || typeof document.fire !== 'function') return;
      if (window.JotForm && window.JotForm.currentSection) return; // already up

      var wanted = jotFormDollar();
      if (wanted) window.$ = wanted;

      try {
        document.loaded = true;
        document.fire('dom:loaded');
      } catch (err) {
        window.console && console.error('[cc-twist] Prototype dom:loaded failed', err);
      }
    }

    /* ----------------------------------------------------------------------
       JotForm sets a few things on globals that belong to the whole document.
       Scope them back down so the rest of the theme keeps working.
       ---------------------------------------------------------------------- */
    function tameJotFormGlobals() {
      applyFlags();

      // This file is deferred, so it runs before DOMContentLoaded -- i.e.
      // before JotForm's own init, which overwrites noJump with
      // `"nojump" in document.get` and installs document.onkeypress in
      // handlePages(). A macrotask lands after both, so re-apply there.
      window.setTimeout(function () {
        applyFlags();
        scopeKeypressToForm();
      }, 0);
    }

    function applyFlags() {
      if (!window.JotForm) return;

      // Land on our step-2 banner instead of JotForm's scrollTo(0, 0).
      window.JotForm.noJump = true;

      // JotForm patches HTMLFormElement.prototype.submit globally and makes it
      // return false whenever THIS form is invalid -- which silently breaks
      // every other form on the page (search, newsletter, currency). Our flow
      // validates through the submit event, so we can disarm the patch.
      window.JotForm.ignoreDirectValidation = true;
    }

    // handlePages() assigns document.onkeypress directly, swallowing Enter
    // everywhere on the page, including the theme's search box. Re-scope it
    // to this form, where blocking Enter is actually wanted (it stops Enter
    // from submitting step 1).
    function scopeKeypressToForm() {
      var jotHandler = document.onkeypress;
      if (typeof jotHandler !== 'function' || jotHandler.__ccScoped) return;

      var scoped = function (e) {
        var target = (e && (e.target || e.srcElement)) || null;
        if (target && form.contains(target)) return jotHandler.call(this, e);
        return true;
      };
      scoped.__ccScoped = true;

      document.onkeypress = scoped;
    }

    /* ----------------------------------------------------------------------
       The Vex app (vex-auto-add-to-cart) injects

         <style id="v-cursor-style-nav">
           … , [href], [href] * { pointer-events: none !important }

       to freeze the UI while it works, and removes it when it finishes. On this
       page it never finishes, so the rule stays and EVERY <a href> on the page
       stops responding to clicks -- header, nav and footer included -- the
       moment the page finishes loading. Nothing is overlaid and no handler
       cancels the click; the anchors simply stop being hit-testable, which is
       also why the cursor never turns into a pointer over them.

       snippets/cc-twist-form-head.liquid fixes the cause (the uncaught
       TypeError that strands Vex mid-run). This is the backstop, for the case
       where Vex strands itself for some other reason.
       ---------------------------------------------------------------------- */
    function dropStaleVexBlocker() {
      var blocker = document.getElementById('v-cursor-style-nav');
      if (blocker) blocker.remove();
    }

    /* ----------------------------------------------------------------------
       Step sync. JotForm toggles page sections by adding/removing
       "js-non-displayed-page" and writing inline styles, so we observe the
       result rather than hooking the next button (whose click may fail
       validation and not advance at all).
       ---------------------------------------------------------------------- */
    function watchSteps() {
      if (!pages.length) return;

      var observer = new MutationObserver(syncStep);
      Array.prototype.forEach.call(pages, function (page) {
        observer.observe(page, { attributes: true, attributeFilter: ['style', 'class'] });
      });

      syncStep();
    }

    function isVisible(el) {
      if (!el) return false;
      if (el.classList.contains('js-non-displayed-page')) return false;
      return window.getComputedStyle(el).display !== 'none';
    }

    function syncStep() {
      if (stage.getAttribute('data-cc-twist-step') === '3') return; // terminal
      setStep(isVisible(pages[0]) ? 1 : 2);
    }

    function setStep(n) {
      var next = String(n);
      if (stage.getAttribute('data-cc-twist-step') === next) return;

      stage.setAttribute('data-cc-twist-step', next);

      if (status) {
        status.textContent = status.getAttribute('data-step-' + next) || '';
      }

      var heading = stage.querySelector('[data-cc-step="' + next + '"] [data-cc-focus]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }

      var top = stage.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /* ----------------------------------------------------------------------
       Single-select. The inputs stay type="checkbox" with name
       "q13_input13[]" so JotForm still recognises the field; we just clear
       the siblings. Assigning .checked programmatically does not dispatch
       "change", so there is no recursion.
       ---------------------------------------------------------------------- */
    function wireSingleSelect() {
      stage.addEventListener('change', function (e) {
        var target = e.target;
        if (!target || !target.matches || !target.matches(VOTE_SELECTOR)) return;

        if (target.checked) {
          Array.prototype.forEach.call(
            stage.querySelectorAll(VOTE_SELECTOR),
            function (other) {
              if (other !== target) other.checked = false;
            }
          );
        }

        Array.prototype.forEach.call(
          stage.querySelectorAll('[data-cc-flavor]'),
          function (card) {
            var input = card.querySelector(VOTE_SELECTOR);
            card.classList.toggle('is-voted', !!(input && input.checked));
          }
        );
      });
    }

    /* ----------------------------------------------------------------------
       "Choose one flavor" is enforced here rather than with JotForm's
       validate[required]: its group-aware check only runs for containers with
       data-type="control_inline", and the fallback path would require all
       three options to be ticked. The error is raised through JotForm.errored()
       so it renders and clears exactly like a native one.

       The listener is capture-phase on the button, so it runs before the
       button's default action and before JotForm's submit handling.
       ---------------------------------------------------------------------- */
    function wireVoteGuard() {
      var button = document.getElementById('input_7');
      var group = stage.querySelector('[data-cc-vote-group]');
      if (!button || !group) return;

      button.addEventListener(
        'click',
        function (e) {
          if (stage.querySelector(VOTE_SELECTOR + ':checked')) return;

          e.preventDefault();
          e.stopImmediatePropagation();

          var first = stage.querySelector(VOTE_SELECTOR);
          if (first && window.JotForm && typeof window.JotForm.errored === 'function') {
            window.JotForm.errored(first, group.getAttribute('data-cc-vote-error') || 'Please select one flavor.');
          }

          group.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        true
      );

      // Clear the error as soon as a flavor is picked.
      stage.addEventListener('change', function (e) {
        if (!e.target || !e.target.matches || !e.target.matches(VOTE_SELECTOR)) return;
        if (!e.target.checked) return;
        if (window.JotForm && typeof window.JotForm.corrected === 'function') {
          window.JotForm.corrected(e.target);
        }
      });
    }

    /* ----------------------------------------------------------------------
       The consent label contains links. A click on a link inside a
       <label for> would otherwise toggle the checkbox instead of opening it.
       ---------------------------------------------------------------------- */
    function wireConsentLinks() {
      // Capture phase on purpose: a <label for> toggles its checkbox when any
      // descendant is clicked, and JotForm binds its own handlers to
      // .form-checkbox-item. In the bubble phase this ran too late (or not at
      // all, if JotForm stopped propagation first), so clicking Official Rules
      // or Privacy Policy just ticked the box instead of opening the page.
      stage.addEventListener('click', function (e) {
        var node = e.target;
        if (node && node.nodeType === 3) node = node.parentNode;
        var link = node && node.closest ? node.closest('.form-checkbox-item label a') : null;
        if (!link || !link.href) return;

        // Cancels the label activation. The anchor's own navigation goes with
        // it, so open the page here instead.
        e.preventDefault();
        e.stopPropagation();

        // No feature string: with one, Chrome opens a popup window (commonly
        // blocked) rather than a tab. rel="noopener" is already on the anchor;
        // null the opener too for the browsers that ignore it on window.open.
        var opened = window.open(link.href, '_blank');
        if (opened) {
          try { opened.opener = null; } catch (err) { /* cross-origin */ }
        } else {
          // Popup blocked. Step 1 has nothing worth preserving yet, so a
          // same-tab navigation is better than the click doing nothing.
          window.location.href = link.href;
        }
      }, true);
    }

    /* ----------------------------------------------------------------------
       Same-page submit.

       JotForm's EventObserver always calls preventDefault() and then the
       native HTMLFormElement.prototype.submit(), which honours form.target --
       so pointing the form at a hidden iframe keeps the whole JotForm submit
       path intact while the top-level page stays put.
       ---------------------------------------------------------------------- */
    function wireSubmit() {
      if (!sink) return; // no iframe -> fall back to JotForm's own thank-you page

      var state = 'idle'; // idle -> sending -> done
      var timer = null;

      form.setAttribute('target', 'cc-twist-sink');

      // Primary signal: dispatched only after validation passes, immediately
      // before the native submit.
      document.addEventListener('EventObserver-form-submitted', function (e) {
        if (e.detail && e.detail.form && e.detail.form !== form) return;
        beginSend();
      });

      // Fallback for the case where EventObserver is switched off. It is
      // deliberately NOT armed when EventObserver is active: sniffing error
      // classes after a failed step 2 validation would advance to the
      // confirmation without a vote ever being recorded.
      if (!window.enableEventObserver) {
        form.addEventListener('submit', function () {
          window.setTimeout(function () {
            if (state !== 'idle') return;
            if (stage.querySelector('.form-line-error, .form-validation-error, .form-button-error')) return;
            beginSend();
          }, 400);
        });
      }

      sink.addEventListener('load', function () {
        // Swallows the initial about:blank load, because state is only
        // 'sending' once a real submit has started.
        if (state !== 'sending') return;
        finish('load');
      });

      function beginSend() {
        if (state !== 'idle') return; // guards against a double submit
        state = 'sending';
        stage.classList.add('is-submitting');

        var button = document.getElementById('input_7');
        if (button) button.setAttribute('aria-busy', 'true');

        timer = window.setTimeout(function () { finish('timeout'); }, SUBMIT_TIMEOUT_MS);
      }

      function finish(reason) {
        if (state !== 'sending') return;
        state = 'done';
        window.clearTimeout(timer);

        stage.classList.remove('is-submitting');
        stage.setAttribute('data-cc-twist-finish', reason);

        if (reason === 'timeout') {
          // The POST already left the browser; only the response never came
          // back. Showing an error here would invite a duplicate submission.
          window.console && console.warn(
            '[cc-twist] No response from JotForm within ' + SUBMIT_TIMEOUT_MS +
            'ms. Showing the confirmation anyway.'
          );
        }

        setStep(3);
      }
    }

    /* ----------------------------------------------------------------------
       Share buttons. UI only -- nothing is recorded back to JotForm.

       Which image gets shared/downloaded depends on which flavor the
       visitor voted for: cc-twist-form.liquid stamps data-cc-share-image /
       data-cc-share-filename onto that flavor's vote checkbox.

       Mobile row (Instagram/X/Email): there's no web intent for posting an
       image straight into those apps, so each button tries the Web Share
       API with the image attached -- the phone's own share sheet does the
       app-to-app handoff. Where a browser can't share files, the anchor's
       own href (profile link / text-only intent / mailto) fires instead.

       Desktop big button: no app to hand the image to, so it just
       downloads it.

       iOS Safari requires navigator.share() to be called synchronously
       inside the click handler -- the user-activation token it relies on
       does not survive an awaited fetch(). So the voted image is fetched
       ahead of time (as soon as a flavor is picked, in wireSingleSelect())
       and cached here; the click handler only ever calls navigator.share()
       with an already-resolved File, never behind a .then(). Same reasoning
       applies to the window.open() fallback, which is why it only fires
       from the synchronous branch below.
       ---------------------------------------------------------------------- */
    function wireShare() {
      var shareStatus = stage.querySelector('[data-cc-share-status]');
      var shareRow = stage.querySelector('[data-cc-share-row]');
      var shareFileCache = {}; // vote value -> resolved File

      stage.addEventListener('change', function (e) {
        if (!e.target || !e.target.matches || !e.target.matches(VOTE_SELECTOR)) return;
        if (e.target.checked) prefetchShareFile();
      });
      prefetchShareFile(); // covers a flavor already checked on load (bfcache, etc.)

      stage.addEventListener('click', function (e) {
        var copyButton = e.target.closest ? e.target.closest('[data-cc-copy]') : null;
        if (copyButton) {
          copyLink(copyButton, shareStatus);
          return;
        }

        var shareButton = e.target.closest ? e.target.closest('[data-cc-share-kind]') : null;
        if (shareButton) {
          handleAppShareClick(e, shareButton);
          return;
        }
      });

      var bigButton = stage.querySelector('[data-cc-share-big]');
      if (bigButton) {
        bigButton.addEventListener('click', function () {
          var vote = currentVoteShare();
          if (!vote) return;

          var cachedFile = shareFileCache[vote.value];
          if (cachedFile) {
            triggerFileDownload(cachedFile, vote.filename, bigButton, shareStatus);
          } else {
            downloadImage(vote.imageUrl, vote.filename, bigButton, shareStatus);
          }
        });
      }

      /* The image the visitor voted for, read off the checked vote input. */
      function currentVoteShare() {
        var input = stage.querySelector(VOTE_SELECTOR + ':checked');
        var imageUrl = input && input.getAttribute('data-cc-share-image');
        if (!input || !imageUrl) return null;

        return {
          value: input.value,
          imageUrl: imageUrl,
          filename: input.getAttribute('data-cc-share-filename') || 'cc-twist-share.jpg'
        };
      }

      function prefetchShareFile() {
        var vote = currentVoteShare();
        if (!vote || shareFileCache[vote.value]) return;
        fetchImageFile(vote.imageUrl, vote.filename).then(function (file) {
          shareFileCache[vote.value] = file;
        });
      }

      function canShareFiles() {
        if (!navigator.canShare) return false;
        try {
          var probe = new File([''], 'probe.jpg', { type: 'image/jpeg' });
          return navigator.canShare({ files: [probe] });
        } catch (err) {
          return false;
        }
      }

      function handleAppShareClick(e, button) {
        if (!canShareFiles()) return; // let the anchor's own href/mailto fire

        var vote = currentVoteShare();
        if (!vote) return; // no image resolved -- fall back to the plain link

        var text = (shareRow && shareRow.getAttribute('data-share-text')) || '';
        var url = (shareRow && shareRow.getAttribute('data-share-url')) || window.location.href;
        var cachedFile = shareFileCache[vote.value];

        if (!cachedFile) {
          /* Prefetch hasn't resolved yet (visitor clicked unusually fast).
             Falling through to the plain link is more reliable than an
             async share() call that iOS will likely drop silently. */
          return;
        }

        e.preventDefault();
        navigator
          .share({ files: [cachedFile], title: document.title, text: text, url: url })
          .catch(function () {
            /* AbortError on cancel, or share() rejected outright -- either
               way, send them through the button's own link. */
            if (button.tagName === 'A') {
              window.open(button.href, button.target || '_blank', 'noopener');
            }
          });
      }
    }

    function fetchImageFile(url, filename) {
      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('cc-twist share image fetch failed');
          return response.blob();
        })
        .then(function (blob) {
          return new File([blob], filename || 'cc-twist-share.jpg', { type: blob.type || 'image/jpeg' });
        });
    }

    function triggerFileDownload(file, filename, button, shareStatus) {
      var objectUrl = URL.createObjectURL(file);
      var link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || file.name || 'cc-twist-share.jpg';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 1000);

      button.classList.add('is-copied');
      if (shareStatus) shareStatus.textContent = 'Downloaded!';

      window.clearTimeout(button._ccDownloadTimer);
      button._ccDownloadTimer = window.setTimeout(function () {
        button.classList.remove('is-copied');
        if (shareStatus) shareStatus.textContent = '';
      }, 2000);
    }

    function downloadImage(url, filename, button, shareStatus) {
      fetchImageFile(url, filename)
        .then(function (file) {
          triggerFileDownload(file, filename, button, shareStatus);
        })
        .catch(function () {
          /* CORS or network failure -- open the image so they can save it by hand. */
          window.open(url, '_blank', 'noopener');
        });
    }

    function copyLink(button, shareStatus) {
      var text = button.getAttribute('data-cc-copy') || window.location.href;
      var done = function () {
        button.classList.add('is-copied');
        if (shareStatus) shareStatus.textContent = button.getAttribute('data-copied-label') || 'Copied!';

        window.clearTimeout(button._ccCopyTimer);
        button._ccCopyTimer = window.setTimeout(function () {
          button.classList.remove('is-copied');
          if (shareStatus) shareStatus.textContent = '';
        }, 2000);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done, function () { legacyCopy(text, done, shareStatus); });
        return;
      }

      legacyCopy(text, done, shareStatus);
    }

    function legacyCopy(text, done, shareStatus) {
      try {
        var field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:-9999px;opacity:0';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        document.body.removeChild(field);
        done();
      } catch (err) {
        if (shareStatus) shareStatus.textContent = text;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
